import * as React from 'react';
import { useState, useContext } from 'react';
import {
  Box,
  Dialog,
  useTheme,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';

import { UserContext } from '../store/userContext';
import { getRowMenuPosition, type MenuPosition } from '../utils/menuPosition';
import UserForm from './UserForm';
import DeleteDialog from '../utils/components/DeleteDialog';

interface UserColumnProps {
  filteredUsers: User[];
  selectedUserId: number | null;
  onUserClick: (user: User) => void;
}

const UserColumn: React.FC<UserColumnProps> = ({
  filteredUsers,
  selectedUserId,
  onUserClick,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const {
    getUsers,
    editUser,
    editUserLoading,
    deleteUser,
    deleteUserLoading,
  } = useContext(UserContext);

  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [hoveredUserId, setHoveredUserId] = useState<number | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: User) => {
    event.stopPropagation();
    setMenuPosition(getRowMenuPosition(event, '[data-user-row="true"]'));
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setMenuPosition(null);
  };

  const handleEditClick = () => {
    setEditOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setDeleteOpen(true);
    handleMenuClose();
  };

  const handleEditSubmit = (data: UserFormDto) => {
    if (selectedUser) {
      editUser(selectedUser.id, data, () => {
        setEditOpen(false);
        getUsers(true);
      });
    }
  };

  const handleDeleteConfirm = () => {
    if (selectedUser) {
      deleteUser(selectedUser.id, () => {
        setDeleteOpen(false);
        getUsers(true);
      });
    }
  };
  const UserNameColumn = ({ children }: { children: React.ReactNode }) => (
      <Grid
          size={{ xs: 3 }}
          sx={{
            borderRight: theme.palette.border,
            p: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
      >
        {children}
      </Grid>
  );

  return (
      <UserNameColumn>
      {filteredUsers.map((user) => (
        <Box
          key={user.id}
          data-user-row="true"
          onClick={() => onUserClick(user)}
          onMouseEnter={() => setHoveredUserId(user.id)}
          onMouseLeave={() => setHoveredUserId((current) => (current === user.id ? null : current))}
          sx={{
            display: 'flex',
            height: '30px',
            minHeight: '30px',
            paddingLeft: '12px',
            alignItems: 'center',
            gap: '10px',
            alignSelf: 'stretch',
            ...theme.typography.usersFontTitle,
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' },
            margin: 0,
            mb: 1,
            backgroundColor: selectedUserId === user.id ? theme.palette.selectedBackground : 'transparent',
            borderRadius: '4px',
          }}
        >
          <Box
            component="span"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flexGrow: 1,
            }}
          >
            {user.email}
          </Box>
          {(hoveredUserId === user.id || (Boolean(menuPosition) && selectedUser?.id === user.id)) && (
            <IconButton
              size="small"
              onClick={(e) => handleMenuOpen(e, user)}
              sx={{ color: 'green' }}
            >
              <MoreVertIcon />
            </IconButton>
          )}
        </Box>
      ))}

      <Menu
        anchorReference="anchorPosition"
        anchorPosition={menuPosition || undefined}
        open={Boolean(menuPosition)}
        onClose={handleMenuClose}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={handleEditClick}>
          <ListItemIcon>
            <EditIcon fontSize="small" sx={{ color: 'green' }} />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: 'green' }} />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)}>
        {selectedUser && (
          <UserForm
            onSubmit={handleEditSubmit}
            defaultValues={selectedUser}
            buttonText={t('common:t.edit')}
            loading={editUserLoading}
            cancelButton
            onCancel={() => setEditOpen(false)}
          />
        )}
      </Dialog>

      <DeleteDialog
        loading={deleteUserLoading}
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        ns="users"
        onConfirm={handleDeleteConfirm}
      />
      </UserNameColumn>
  );
};

export default UserColumn;
